/**
 * A product sold in more than one size — `FRONTEND.md` §9.85, `BACKEND.md` §10A.33.
 *
 * 30 of the owner's products carry a `pizza_sizes` array holding a **label**, a **price** and a
 * **weight** each, and both apps drew `products.price` alone — the small one. So a 50 cm pizza could
 * not be ordered at all. The owner: *"I don't see sizes, only one size, I don't see weights for that
 * specific price… that's a big nono."*
 *
 * The column is a JSON string on the wire. Parsing it is the same job in both apps, so it lives here
 * with the rest of what §5 says two modules share.
 */

import { parseMoneyToCents } from './money'

export interface ProductSize {
  /** As the menu spells it — `"50 cm"`. This is what the order line names, and the server matches. */
  label: string
  priceCents: number
  /** Grams, or null where the menu did not say. Drawn beside the price; never priced from. */
  weightG: number | null
}

/** Only what this needs; products arrive from the generated client with far more on them. */
export interface SizedProduct {
  pizzaSizes?: string | null
}

function weightOf(raw: unknown): number | null {
  // Only the two shapes the column actually holds — `"560"` and `560`. Anything else is not a
  // weight, and stringifying it would print `[object Object]` into the card.
  if (typeof raw === 'number') return Number.isFinite(raw) && raw > 0 ? raw : null
  if (typeof raw !== 'string' || raw.trim() === '') return null
  const grams = Number(raw.replace(',', '.'))
  return Number.isFinite(grams) && grams > 0 ? grams : null
}

/**
 * Whether this size has been given a price at all - §9.378.
 *
 * The server's own test, in its own order: a blank string is not a price (`price.isBlank()`), and
 * neither is something that cannot be read as a number (`NumberFormatException`). `0` passes both
 * there and here.
 */
function hasAPrice(raw: unknown): boolean {
  if (typeof raw === 'number') return Number.isFinite(raw)
  if (typeof raw !== 'string') return false
  const text = raw.trim()
  if (text === '') return false
  return Number.isFinite(Number(text.replace(',', '.')))
}

/**
 * The sizes this product is sold in, in the menu's own order, or empty.
 *
 * **Empty is the answer for almost every product**, and the caller draws its single price exactly as
 * it always has. A size with no label is dropped: the label is what the order names and what
 * `ServerPricing` matches on, so one without it could not be ordered — and offering a button that
 * the server would refuse is worse than not offering it.
 *
 * Malformed JSON costs the product its sizes rather than the page: a menu somebody typed badly must
 * not stop a customer ordering everything else.
 */
export function productSizes(product: SizedProduct | null | undefined): ProductSize[] {
  const raw = product?.pizzaSizes
  if (typeof raw !== 'string' || raw.trim() === '') return []

  let parsed: unknown
  try {
    parsed = JSON.parse(raw)
  } catch {
    return []
  }
  if (!Array.isArray(parsed)) return []

  const sizes: ProductSize[] = []
  for (const entry of parsed) {
    if (entry === null || typeof entry !== 'object') continue
    const row = entry as { label?: unknown; price?: unknown; weightG?: unknown }
    const label = typeof row.label === 'string' ? row.label.trim() : ''
    if (label === '') continue
    // §9.378, FB-673 - **and a size with no price is dropped for the same reason.** `ServerPricing`
    // skips a row whose price is blank (`pricesByLabel`) and then **refuses the whole order** when a
    // line names it, which is the owner's *"Táto veľkosť už nie je v ponuke"* on a till that was
    // offering the button at 0,00 €. The paragraph above already says why: offering one the server
    // would refuse is worse than not offering it.
    //
    // **An explicit `0` is kept**, because the server keeps it too and prices the line at nothing: a
    // franchise that really sells a size for nothing is not the case this fixes.
    if (!hasAPrice(row.price)) continue
    sizes.push({
      label,
      priceCents: parseMoneyToCents(row.price as string | number | null),
      weightG: weightOf(row.weightG),
    })
  }
  return sizes
}

/**
 * Whether a product must be chosen by size rather than added at one price.
 *
 * One size is not a choice — the card draws it as an ordinary product, because a picker offering a
 * single option is a control that cannot be used.
 *
 * **Takes the sizes, not the product**, because every caller has already parsed them: `pizzaSizes` is
 * JSON on the row, and asking the product again would parse it a second time for every card in a
 * hundred-product grid.
 */
export function offersSizeChoice(sizes: readonly ProductSize[]): boolean {
  return sizes.length > 1
}

/** `560` → `560 g`, for the line beside the price. Null where the menu gave no weight. */
export function sizeWeightText(size: ProductSize): string | null {
  return size.weightG === null ? null : `${String(size.weightG)} g`
}
